(() => {
  const ROOT_SELECTOR = '[data-survey-form]';
  const MAX_FILES = 4;
  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  const MAX_TOTAL_FILE_SIZE = 15 * 1024 * 1024;
  const SEARCH_TIMEOUT = 6000;
  const SUBMIT_MODAL_MIN_VISIBLE = 700;

  async function fetchWithTimeout(url, options = {}, timeout = SEARCH_TIMEOUT) {
    const controller = new AbortController();
    const parentSignal = options.signal;
    let timedOut = false;
    const forwardAbort = () => controller.abort();

    if (parentSignal) {
      if (parentSignal.aborted) controller.abort();
      else parentSignal.addEventListener('abort', forwardAbort, { once: true });
    }

    const timer = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeout);

    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } catch (error) {
      if (timedOut) throw new Error('Search request timed out');
      throw error;
    } finally {
      window.clearTimeout(timer);
      if (parentSignal) parentSignal.removeEventListener('abort', forwardAbort);
    }
  }

  function initializeSurveyForm(root) {
    if (!root || root.dataset.surveyInitialized === 'true') return;
    root.dataset.surveyInitialized = 'true';

    const form = root.querySelector('form');
    if (!form) return;
    form.noValidate = true;

    const panels = Array.from(root.querySelectorAll('[data-step-panel]'));
    const indicators = Array.from(root.querySelectorAll('[data-step-indicator]'));
    const confirmation = root.querySelector('[data-confirmation]');
    const submitStatus = root.querySelector('[data-submit-status]');
    const submitButton = root.querySelector('[data-submit-button]');
    const submitLabel = root.querySelector('[data-submit-label]');
    const confirmationAcknowledgement = root.querySelector('[data-confirmation-acknowledgement]');
    const submitModal = root.querySelector('[data-submit-modal]');
    const submitModalDialog = root.querySelector('[data-submit-modal-dialog]');
    const submitModalSending = root.querySelector('[data-submit-modal-sending]');
    const submitModalSuccess = root.querySelector('[data-submit-modal-success]');
    const submitAgainButton = root.querySelector('[data-submit-again]');
    const endpoint = root.dataset.submissionEndpoint || '';
    let currentStep = Number(root.dataset.initialStep || 0);
    let confirmationUrls = [];
    let isSubmitting = false;
    let submitModalOpenedAt = 0;
    let submitModalCloseTimer;
    let questionController;

    function syncSubmitButton() {
      submitButton.disabled = !confirmationAcknowledgement?.checked || isSubmitting || !endpoint;
      if (confirmationAcknowledgement) confirmationAcknowledgement.disabled = isSubmitting;
    }

    if (confirmationAcknowledgement) {
      // Do not reuse a restored checkbox value for a new inquiry.
      confirmationAcknowledgement.checked = false;
      confirmationAcknowledgement.addEventListener('change', () => {
        clearStepError(panels.length - 1);
        syncSubmitButton();
      });
    }

    function showStep(step, options = {}) {
      questionController?.cancelAdvance();
      const nextStep = Math.max(0, Math.min(step, panels.length - 1));
      if (nextStep !== currentStep && confirmationAcknowledgement) confirmationAcknowledgement.checked = false;
      currentStep = nextStep;
      syncSubmitButton();

      panels.forEach((panel, index) => {
        const active = index === nextStep;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      indicators.forEach((indicator, index) => {
        indicator.classList.toggle('is-current', index === nextStep);
        indicator.classList.toggle('is-complete', index < nextStep);
        if (index === nextStep) {
          indicator.setAttribute('aria-current', 'step');
        } else {
          indicator.removeAttribute('aria-current');
        }
      });

      if (nextStep === panels.length - 1) renderConfirmation();
      if (panels[nextStep].dataset.stepKind === 'details' && questionController) {
        questionController.showCurrent(options.focus !== false);
        return;
      }

      if (options.focus !== false) {
        const heading = panels[nextStep].querySelector('h2');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }

        const bounds = root.getBoundingClientRect();
        if (bounds.top < 0 || bounds.top > window.innerHeight * 0.65) {
          root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    function clearStepError(step) {
      const panel = panels[step];
      if (!panel) return;
      const message = panel.querySelector('[data-step-error]');
      if (message) {
        message.hidden = true;
        message.textContent = '';
      }
      panel.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));
    }

    function setStepError(step, text, field) {
      const panel = panels[step];
      const message = panel && panel.querySelector('[data-step-error]');
      if (message) {
        message.textContent = text;
        message.hidden = false;
      }
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        if (typeof field.focus === 'function') field.focus();
      }
    }

    function validateStep(step) {
      clearStepError(step);
      const panel = panels[step];
      if (!panel) return true;
      if (panel.dataset.stepKind === 'details' && questionController) return questionController.validateAll();

      if (step === panels.length - 1 && !confirmationAcknowledgement?.checked) {
        setStepError(step, '上記の内容をご確認のうえ、チェックを入れてください。', confirmationAcknowledgement);
        return false;
      }

      const invalidField = Array.from(panel.querySelectorAll('input[required], select[required], textarea[required]'))
        .find((field) => !field.matches('[data-image-input]') && (!String(field.value || '').trim() || !field.checkValidity()));

      if (invalidField) {
        const message = invalidField.validity.typeMismatch
          ? '正しい形式のメールアドレスを入力してください。'
          : '必須項目を入力してください。';
        setStepError(step, message, invalidField);
        return false;
      }

      return true;
    }

    root.querySelectorAll('[data-next-step]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = button.closest('[data-step-panel]');
        const step = Number(panel.dataset.stepPanel);
        if (panel.dataset.stepKind === 'details' && questionController) {
          questionController.next();
          return;
        }
        if (validateStep(step)) showStep(step + 1);
      });
    });

    root.querySelectorAll('[data-prev-step]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = button.closest('[data-step-panel]');
        if (panel.dataset.stepKind === 'details' && questionController) {
          questionController.previous();
          return;
        }
        if (panel.dataset.stepKind === 'confirmation') questionController?.last();
        showStep(Number(panel.dataset.stepPanel) - 1);
      });
    });

    panels.forEach((panel, step) => {
      panel.addEventListener('input', (event) => {
        if (event.target.matches('input, select, textarea')) clearStepError(step);
      });
    });


    const attachmentController = setupImageAttachments(root);
    const orderController = setupOrderHistory(root);
    const nameController = setupCustomerName(root, () => clearStepError(0));
    setupLoginModal(root);
    questionController = setupQuestionnaire(root, {
      complete: () => showStep(panels.length - 1),
      back: () => showStep(0),
      clearError: () => clearStepError(1),
      error: (message, field) => setStepError(1, message, field),
      uploadState: () => attachmentController.navigationState()
    });
    attachmentController.onChange?.(() => {
      clearStepError(1);
      questionController?.syncNavigation();
    });

    function getConfirmationValue(field) {
      let value = String(field.value || '').trim();

      if (field.tagName === 'SELECT' && value) {
        const selectedOption = field.options[field.selectedIndex];
        value = selectedOption ? selectedOption.textContent.trim() : value;
      }

      const suffix = field.dataset.confirmSuffix || '';
      return value ? `${value}${suffix}` : '—';
    }

    function createConfirmationRow(label) {
      const row = document.createElement('div');
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      row.className = 'survey__confirmation-row';
      term.textContent = label;
      row.append(term, description);
      confirmation.appendChild(row);
      return description;
    }

    function renderConfirmation() {
      if (!confirmation) return;

      confirmationUrls.forEach((url) => URL.revokeObjectURL(url));
      confirmationUrls = [];
      confirmation.replaceChildren();

      form.querySelectorAll('[data-confirm-label]').forEach((field) => {
        if (field.disabled) return;
        if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) return;
        const description = createConfirmationRow(field.dataset.confirmLabel);
        description.textContent = getConfirmationValue(field);
      });
      questionController?.answers().forEach(({ label, value }) => {
        createConfirmationRow(label).textContent = value || '—';
      });

      const files = attachmentController.files();
      const imageDescription = createConfirmationRow(questionController ? 'Q19. 添付画像' : '添付画像');

      if (!files.length) {
        imageDescription.textContent = '—';
        return;
      }

      const imageList = document.createElement('div');
      imageList.className = 'survey__confirmation-images';
      files.forEach((file, index) => {
        const figure = document.createElement('figure');
        const image = document.createElement('img');
        const caption = document.createElement('figcaption');
        const url = URL.createObjectURL(file);

        confirmationUrls.push(url);
        figure.className = 'survey__confirmation-image';
        image.src = url;
        image.alt = `添付画像 ${index + 1}`;
        caption.textContent = file.name;
        figure.append(image, caption);
        imageList.appendChild(figure);
      });
      imageDescription.appendChild(imageList);
      const faceBlur = attachmentController.preference?.();
      if (faceBlur) createConfirmationRow('お顔ぼかし処理について').textContent = faceBlur;
    }

    function validateAllSteps() {
      for (let step = 0; step < panels.length; step += 1) {
        if (!validateStep(step)) {
          showStep(step, { focus: false });
          const invalid = panels[step].querySelector('[aria-invalid="true"]');
          if (invalid && typeof invalid.focus === 'function') invalid.focus();
          return false;
        }
      }
      return true;
    }

    function setSubmitStatus(message, type) {
      submitStatus.textContent = message;
      submitStatus.className = `survey__notice survey__notice--${type}`;
      submitStatus.hidden = false;
    }

    function openSubmitModal() {
      if (!submitModal) return;
      window.clearTimeout(submitModalCloseTimer);
      if (submitModalSending) submitModalSending.hidden = false;
      if (submitModalSuccess) submitModalSuccess.hidden = true;
      submitModal.classList.remove('is-complete');
      if (submitModalDialog) {
        const sendingTitle = submitModalSending && submitModalSending.querySelector('.survey-submit-modal__title');
        const sendingText = submitModalSending && submitModalSending.querySelector('.survey-submit-modal__text');
        submitModalDialog.setAttribute('aria-busy', 'true');
        if (sendingTitle) submitModalDialog.setAttribute('aria-labelledby', sendingTitle.id);
        if (sendingText) submitModalDialog.setAttribute('aria-describedby', sendingText.id);
      }
      submitModalOpenedAt = performance.now();
      submitModal.hidden = false;
      submitModal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('survey-submit-modal-open');

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          submitModal.classList.add('is-open');
          if (submitModalDialog) submitModalDialog.focus({ preventScroll: true });
        });
      });
    }

    function showSubmitSuccessModal(warning) {
      if (!submitModal || !submitModalSuccess) return;
      if (submitModalSending) submitModalSending.hidden = true;
      submitModalSuccess.hidden = false;
      submitModal.classList.add('is-complete');

      if (submitModalDialog) {
        const successTitle = submitModalSuccess.querySelector('.survey-submit-modal__title');
        const successText = submitModalSuccess.querySelector('.survey-submit-modal__text[id]');
        submitModalDialog.setAttribute('aria-busy', 'false');
        if (successTitle) submitModalDialog.setAttribute('aria-labelledby', successTitle.id);
        if (successText) {
          successText.textContent = warning || 'アンケートへのご協力ありがとうございました。';
          submitModalDialog.setAttribute('aria-describedby', successText.id);
        }
      }

      window.requestAnimationFrame(() => {
        if (submitAgainButton) submitAgainButton.focus({ preventScroll: true });
      });
    }

    async function waitForSubmitModalAnimation() {
      if (!submitModalOpenedAt) return;
      const remaining = SUBMIT_MODAL_MIN_VISIBLE - (performance.now() - submitModalOpenedAt);
      if (remaining > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, remaining));
      }
    }

    function closeSubmitModal() {
      if (!submitModal) return;
      submitModal.classList.remove('is-open');
      submitModal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('survey-submit-modal-open');
      submitModalOpenedAt = 0;
      window.clearTimeout(submitModalCloseTimer);
      submitModalCloseTimer = window.setTimeout(() => {
        submitModal.hidden = true;
        submitModal.classList.remove('is-complete');
      }, 420);
    }

    if (submitModal) {
      submitModal.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          return;
        }
        if (event.key !== 'Tab') return;

        const focusable = Array.from(submitModal.querySelectorAll('a[href], button:not([disabled])'))
          .filter((element) => !element.hidden && element.offsetParent !== null);
        if (!focusable.length) {
          event.preventDefault();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    if (submitAgainButton) {
      submitAgainButton.addEventListener('click', () => {
        submitStatus.hidden = true;
        closeSubmitModal();
        showStep(0, { focus: false });
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.setTimeout(() => {
          const firstField = panels[0] && panels[0].querySelector('button, input:not([type="hidden"]), select, textarea');
          if (firstField) firstField.focus({ preventScroll: true });
        }, 120);
      });
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (isSubmitting) {
        return;
      }

      // Enter on an input advances the current step; only confirmation may send.
      if (currentStep < panels.length - 1) {
        if (panels[currentStep].dataset.stepKind === 'details' && questionController) {
          questionController.next();
          return;
        }
        if (validateStep(currentStep)) showStep(currentStep + 1);
        return;
      }

      if (!validateAllSteps()) {
        return;
      }

      if (!endpoint) {
        setSubmitStatus('現在、アンケートの送信設定を準備中です。dress@tweed.co.jp へ直接ご連絡ください。', 'error');
        return;
      }

      isSubmitting = true;
      syncSubmitButton();
      submitButton.setAttribute('aria-busy', 'true');
      submitLabel.textContent = '提出中…';
      submitStatus.hidden = true;
      openSubmitModal();
      let submissionSucceeded = false;
      let customerWarning = '';

      try {
        const formData = new FormData(form);
        formData.delete('images[]');
        attachmentController.files().forEach((file) => formData.append('images[]', file, file.name));

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });

        let responseBody = null;
        if ((response.headers.get('content-type') || '').includes('application/json')) {
          responseBody = await response.json().catch(() => null);
        }

        if (!response.ok || !responseBody || responseBody.success !== true) {
          throw new Error(responseBody && responseBody.message ? responseBody.message : '送信に失敗しました。');
        }

        if (responseBody.customerEmailStatus === 'failed') {
          customerWarning = '回答を受け付けましたが、確認メールを送信できませんでした。再送信は不要です。';
        }

        form.reset();
        attachmentController.reset();
        orderController.reset();
        nameController.reset();
        questionController?.reset();
        panels.forEach((panel, step) => clearStepError(step));
        showStep(0, { focus: false });
        setSubmitStatus(responseBody && responseBody.message ? responseBody.message : root.dataset.successMessage, 'success');
        submissionSucceeded = true;
      } catch (error) {
        setSubmitStatus(error.message || '送信できませんでした。時間をおいて再度お試しください。', 'error');
      } finally {
        await waitForSubmitModalAnimation();
        if (submissionSucceeded) showSubmitSuccessModal(customerWarning);
        else closeSubmitModal();
        isSubmitting = false;
        syncSubmitButton();
        submitButton.removeAttribute('aria-busy');
        submitLabel.textContent = '提出';
        if (!submissionSucceeded) {
          submitStatus.setAttribute('tabindex', '-1');
          submitStatus.focus({ preventScroll: true });
        }
      }
    });

    showStep(currentStep, { focus: false });
  }

  function setupQuestionnaire(root, callbacks) {
    const container = root.querySelector('[data-questionnaire]');
    if (!container) return null;
    const questions = Array.from(container.querySelectorAll('[data-question]'));
    const counter = container.querySelector('[data-question-count]');
    const progress = container.querySelector('[data-question-progress]');
    const nextButton = root.querySelector('[data-question-next]');
    let current = 0;
    let advanceTimer;
    let revision = 0;

    function cancelAdvance() {
      window.clearTimeout(advanceTimer);
      advanceTimer = null;
      revision += 1;
    }

    function selected(question) {
      return Array.from(question.querySelectorAll('input:checked'));
    }

    function uploadState() {
      return callbacks.uploadState?.() || { label: '画像添付しない', waiting: false };
    }

    function syncNavigation() {
      const question = questions[current];
      const state = question.dataset.questionType === 'upload' ? uploadState() : { label: '次へ', waiting: false };
      // Manual ratings always show Next; answered auto-advance radios can also continue unchanged.
      nextButton.hidden = question.dataset.questionType === 'radio' && question.dataset.autoAdvance !== 'false' && !selected(question).length;
      nextButton.textContent = state.label;
      nextButton.disabled = state.waiting;
    }

    function show(index, focus = true) {
      cancelAdvance();
      current = Math.max(0, Math.min(index, questions.length - 1));
      questions.forEach((question, i) => { question.hidden = i !== current; });
      counter.textContent = `${current + 1}/${questions.length}`;
      progress.value = current + 1;
      progress.max = questions.length;
      progress.textContent = counter.textContent;
      const question = questions[current];
      syncNavigation();
      if (focus) {
        const heading = question.querySelector('legend');
        heading.focus({ preventScroll: true });
        const bounds = container.getBoundingClientRect();
        if (bounds.top < 0 || bounds.top > window.innerHeight * 0.65) {
          container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    function validate(index) {
      const question = questions[index];
      if (question.dataset.questionType === 'upload') {
        const state = uploadState();
        if (!state.waiting) return true;
        show(index);
        callbacks.error(state.message || '画像を選択してください。添付しない場合はNGカットの確認チェックを外してください。', state.field || root.querySelector('[data-image-input]'));
        return false;
      }
      if (selected(question).length) return true;
      show(index);
      callbacks.error('回答を選択してください。', question.querySelector('input'));
      return false;
    }

    function next() {
      cancelAdvance();
      callbacks.clearError();
      if (!validate(current)) return;
      if (current < questions.length - 1) show(current + 1);
      else if (validateAll()) callbacks.complete();
    }

    function validateAll() {
      for (let i = 0; i < questions.length; i += 1) {
        if (!validate(i)) return false;
      }
      return true;
    }

    questions.forEach((question, index) => {
      const autoAdvance = (event) => {
        const input = event.target;
        if (question.dataset.questionType !== 'radio' || index !== current || question.hidden || !input.matches('input[type="radio"]') || !input.checked) return;
        cancelAdvance();
        callbacks.clearError();
        if (question.dataset.autoAdvance === 'false') return;
        const scheduledRevision = revision;
        advanceTimer = window.setTimeout(() => {
          if (scheduledRevision === revision && current === index && !question.hidden) next();
        }, 250);
      };
      // change handles keyboard selection; click also handles choosing the same answer after going back.
      question.addEventListener('change', autoAdvance);
      question.addEventListener('click', autoAdvance);
    });

    show(0, false);
    return {
      next,
      previous() {
        cancelAdvance();
        callbacks.clearError();
        if (current > 0) show(current - 1);
        else callbacks.back();
      },
      last: () => show(questions.length - 1, false),
      showCurrent: (focus) => show(current, focus),
      syncNavigation,
      cancelAdvance,
      validateAll,
      answers: () => questions.filter(question => question.dataset.questionType !== 'upload')
        .map(question => ({ label: question.dataset.questionLabel, value: selected(question).map(input => input.value).join('、') })),
      reset: () => show(0, false)
    };
  }

  function setupLoginModal(root) {
    const trigger = root.querySelector('[data-open-login]');
    const modal = root.querySelector('[data-login-modal]');
    if (!trigger || !modal) return;
    const dialog = modal.querySelector('[data-login-dialog]');
    const email = modal.querySelector('[data-login-email]');
    const password = modal.querySelector('[data-login-password]');
    const closeButton = modal.querySelector('[data-close-login]');
    const surveyContent = root.querySelector('.survey__inner');
    const inquiryEmail = root.querySelector('input[name="survey[email]"]');
    let closeTimer;
    let previouslyInert = false;

    function open() {
      window.clearTimeout(closeTimer);
      if (modal.hidden) previouslyInert = Boolean(surveyContent && surveyContent.inert);
      modal.hidden = false;
      if (surveyContent) surveyContent.inert = true;
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('survey-login-modal-open');
      if (!email.value && inquiryEmail) email.value = inquiryEmail.value;
      window.requestAnimationFrame(() => {
        if (trigger.getAttribute('aria-expanded') !== 'true') return;
        modal.classList.add('is-open');
        email.focus({ preventScroll: true });
      });
    }

    function close() {
      modal.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      // Do not retain a password after dismissing the dialog.
      password.value = '';
      if (surveyContent) surveyContent.inert = previouslyInert;
      document.body.classList.remove('survey-login-modal-open');
      trigger.focus({ preventScroll: true });
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => { modal.hidden = true; }, 200);
    }

    trigger.addEventListener('click', (event) => {
      // Preserve the real login URL for new tabs and browsers without this script.
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
      event.preventDefault();
      open();
    });
    closeButton.addEventListener('click', close);
    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });
    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      // Keep normal tab navigation in the dialog; do not trap external CAPTCHA challenges.
      const focusable = Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), iframe'))
        .filter((element) => !element.hidden && element.offsetParent !== null);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    });
    // No submit handler: use Shopify's native POST and CAPTCHA, never the email API.
    if (modal.querySelector('[data-login-errors]')) open();
  }

  function setupOrderHistory(root) {
    const history = root.querySelector('[data-order-history]');
    if (!history) return { reset() {} };
    const number = root.querySelector('[data-order-number]');
    const ordersSelect = history.querySelector('[data-order-select]');
    const more = history.querySelector('[data-orders-more]');
    const status = history.querySelector('[data-orders-status]');
    const orders = new Set();
    let nextUrl = '';
    let pendingRequest;

    function readOrders(source) {
      source.querySelectorAll('[data-history-order]').forEach((node) => {
        const name = node.dataset.number;
        if (!name || orders.has(name)) return;
        orders.add(name);
        const option = document.createElement('option');
        option.value = name;
        option.textContent = node.dataset.label || name;
        ordersSelect.appendChild(option);
      });
      nextUrl = source.dataset.nextUrl || '';
      more.hidden = !nextUrl;
      status.textContent = orders.size ? '' : '注文履歴がありません。購入時のアカウントでログインし直すか、ログアウトして注文番号を入力してください。';
    }

    const initialSource = history.querySelector('[data-order-history-source]');
    if (initialSource) {
      readOrders(initialSource);
      initialSource.remove();
    }

    ordersSelect.addEventListener('change', () => {
      number.value = orders.has(ordersSelect.value) ? ordersSelect.value : '';
    });

    more.addEventListener('click', async () => {
      if (!nextUrl || pendingRequest) return;
      const controller = new AbortController();
      pendingRequest = controller;
      more.disabled = true;
      status.textContent = '注文履歴を読み込み中…';
      try {
        const url = new URL(nextUrl, window.location.href);
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
          throw new Error('Invalid order history URL');
        }
        url.searchParams.set('section_id', root.dataset.sectionId);
        const response = await fetchWithTimeout(url.toString(), {
          credentials: 'same-origin', cache: 'no-store', redirect: 'error',
          headers: { Accept: 'text/html' }, signal: controller.signal
        });
        if (!response.ok) throw new Error('Order history unavailable');
        const html = await response.text();
        if (controller.signal.aborted || pendingRequest !== controller) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector('[data-order-history-source]');
        if (!source) throw new Error('Order history session unavailable');
        const previousSize = orders.size;
        readOrders(source);
        status.textContent = `${orders.size - previousSize}件の注文を追加しました。`;
      } catch (error) {
        if (controller.signal.aborted) return;
        status.textContent = '注文履歴を読み込めませんでした。ログイン状態を確認して再度お試しください。注文番号の選択は必須です。';
      } finally {
        if (pendingRequest === controller) {
          pendingRequest = null;
          more.disabled = false;
        }
      }
    });

    return {
      reset() {
        if (pendingRequest) pendingRequest.abort();
        pendingRequest = null;
        more.disabled = false;
        ordersSelect.value = '';
        number.value = '';
        status.textContent = orders.size ? '' : '注文履歴がありません。購入時のアカウントでログインし直すか、ログアウトして注文番号を入力してください。';
      }
    };
  }

  function setupCustomerName(root, clearError = () => {}) {
    const input = root.querySelector('[data-customer-name]');
    const toggle = root.querySelector('[data-manual-name-toggle]');
    const hint = root.querySelector('[data-name-hint]');
    if (!input || !toggle) return { reset() {} };
    const accountName = (input.dataset.accountName || '').trim();
    let manualDraft = accountName;
    let manual = false;

    function sync(focus = false) {
      // Accounts without a name must still be able to complete the required field.
      if (!accountName) toggle.checked = true;
      toggle.disabled = !accountName;
      const nextManual = toggle.checked;
      if (manual && !nextManual) manualDraft = input.value;
      input.value = nextManual ? manualDraft : accountName;
      input.readOnly = !nextManual;
      manual = nextManual;
      if (hint) hint.textContent = !accountName
        ? 'お名前の登録がないため、手動でご入力ください。'
        : manual ? 'アンケートに使用するお名前をご入力ください。'
          : 'アカウントに登録されたお名前です。';
      clearError();
      if (focus && manual) input.focus({ preventScroll: true });
    }

    toggle.checked = !accountName;
    sync();
    toggle.addEventListener('change', () => sync(true));
    return {
      reset() {
        manualDraft = accountName;
        manual = false;
        toggle.checked = !accountName;
        sync();
      }
    };
  }

  function setupImageAttachments(root) {
    const input = root.querySelector('[data-image-input]');
    const previews = root.querySelector('[data-image-previews]');
    const errorOutput = root.querySelector('[data-file-error]');
    const consent = root.querySelector('[data-upload-consent]');
    const trigger = root.querySelector('[data-image-trigger]');
    const faceBlur = root.querySelector('[data-face-blur]');
    const faceBlurOptions = faceBlur ? Array.from(faceBlur.querySelectorAll('[data-face-blur-option]')) : [];
    let files = [];
    let previewUrls = [];
    let changed = () => {};

    function isAllowed() {
      return !consent || consent.checked;
    }

    function syncState() {
      input.disabled = !isAllowed();
      trigger?.setAttribute('aria-disabled', String(input.disabled));
      previews.hidden = !isAllowed();
      const needsPreference = isAllowed() && files.length > 0;
      if (faceBlur) faceBlur.hidden = !needsPreference;
      faceBlurOptions.forEach((option) => {
        option.disabled = !needsPreference;
        if (!files.length) option.checked = false;
      });
      changed();
    }

    // A newly loaded form must not reuse a browser-restored consent or file selection.
    if (consent) consent.checked = false;
    input.value = '';
    syncState();
    consent?.addEventListener('change', () => {
      showError('');
      syncState();
    });
    faceBlurOptions.forEach((option) => option.addEventListener('change', syncState));

    function preference() {
      return isAllowed() && files.length ? faceBlurOptions.find(option => option.checked)?.value || '' : '';
    }

    function showError(message) {
      errorOutput.textContent = message;
      errorOutput.hidden = !message;
    }

    function syncInputFiles() {
      if (typeof DataTransfer === 'undefined') return;
      const transfer = new DataTransfer();
      files.forEach((file) => transfer.items.add(file));
      input.files = transfer.files;
    }

    function renderPreviews() {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
      previewUrls = [];
      previews.replaceChildren();

      files.forEach((file, index) => {
        const figure = document.createElement('figure');
        const image = document.createElement('img');
        const remove = document.createElement('button');
        const url = URL.createObjectURL(file);

        previewUrls.push(url);
        figure.className = 'survey__preview';
        image.src = url;
        image.alt = `添付画像 ${index + 1}`;
        remove.type = 'button';
        remove.textContent = '×';
        remove.setAttribute('aria-label', `${file.name}を削除`);
        remove.addEventListener('click', () => {
          files.splice(index, 1);
          syncInputFiles();
          renderPreviews();
          showError('');
        });

        figure.append(image, remove);
        previews.appendChild(figure);
      });
      syncState();
    }

    input.addEventListener('change', () => {
      if (!isAllowed()) {
        input.value = '';
        return;
      }
      const incoming = Array.from(input.files || []);
      const known = new Set(files.map((file) => `${file.name}:${file.size}:${file.lastModified}`));
      const messages = [];

      incoming.forEach((file) => {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (known.has(key)) return;
        if (!file.type.startsWith('image/')) {
          messages.push(`${file.name}は画像ファイルではありません。`);
          return;
        }
        if (file.size > MAX_FILE_SIZE) {
          messages.push(`${file.name}は10MBを超えています。`);
          return;
        }
        const totalSize = files.reduce((sum, currentFile) => sum + currentFile.size, 0);
        if (totalSize + file.size > MAX_TOTAL_FILE_SIZE) {
          messages.push('画像の合計容量は15MB以内にしてください。');
          return;
        }
        if (files.length >= MAX_FILES) {
          messages.push(`画像は最大${MAX_FILES}枚までです。`);
          return;
        }
        known.add(key);
        files.push(file);
      });

      syncInputFiles();
      renderPreviews();
      showError(messages.join(' '));
    });

    return {
      // Unticking consent excludes any retained local selections from confirmation and submission.
      files: () => isAllowed() ? files.slice() : [],
      preference,
      navigationState: () => {
        const missingPreference = Boolean(faceBlur) && isAllowed() && files.length > 0 && !preference();
        return {
          label: !consent ? '次へ' : !isAllowed() ? '画像添付しない' : files.length ? '次へ' : '画像添付待ち',
          waiting: (Boolean(consent) && isAllowed() && !files.length) || missingPreference,
          message: missingPreference ? 'お顔ぼかし処理について選択してください。' : '',
          field: missingPreference ? faceBlurOptions[0] : input
        };
      },
      onChange: (listener) => { changed = listener; },
      reset: () => {
        files = [];
        if (consent) consent.checked = false;
        input.value = '';
        renderPreviews();
        showError('');
      }
    };
  }

  function initializeAll(scope = document) {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) initializeSurveyForm(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initializeSurveyForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();

